import Papa from "papaparse";
import { isDangerousTranslationKey } from "./keys";

export function downloadAsCsv(rows: string[][], name: string): void {
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${name.replaceAll("/", "_")}.csv`;
  link.click();
  // Firefox не успевает начать скачивание, если revoke вызвать синхронно
  // сразу после click — откладываем на следующий тик.
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

/**
 * Ошибки Papa, которые не считаем фатальными: UndetectableDelimiter — обычная
 * ситуация для файла из одной колонки, данные при этом читаются корректно.
 */
const IGNORED_PARSE_ERROR_CODES = new Set(["UndetectableDelimiter"]);

function formatParseErrors(errors: Papa.ParseError[]): string {
  const shown = errors
    .slice(0, 3)
    .map((error) =>
      typeof error.row === "number"
        ? `строка ${error.row + 1}: ${error.message}`
        : error.message
    )
    .join("; ");
  const rest = errors.length > 3 ? ` и ещё ${errors.length - 3}` : "";

  return `Не удалось разобрать CSV (${shown}${rest})`;
}

function assertSafeKey(key: string): void {
  if (isDangerousTranslationKey(key)) {
    throw new Error(
      `Ключ «${key}» содержит запрещённый сегмент (__proto__, constructor или prototype) — импорт отменён`
    );
  }
}

/**
 * CSV → {key: value}. Колонки: key,...,value (значение — последняя колонка).
 * Строка-заголовка (первая строка с первой колонкой "key") пропускается —
 * именно первая, чтобы не терять легальный ключ "key" в середине файла.
 * Пустые и пробельные ключи отбрасываются, ключи триммятся.
 */
export function parseCsv(file: File): Promise<Record<string, string>> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result) => {
        const errors = (result.errors ?? []).filter(
          (error) => !IGNORED_PARSE_ERROR_CODES.has(error.code)
        );

        if (errors.length) {
          reject(new Error(formatParseErrors(errors)));
          return;
        }

        const rows = result.data.filter((row) => Array.isArray(row));
        const headerOffset =
          rows.length && (rows[0][0] ?? "").trim().toLowerCase() === "key"
            ? 1
            : 0;

        try {
          const words = rows
            .slice(headerOffset)
            .reduce<Record<string, string>>((all, row) => {
              const key = (row[0] ?? "").trim();

              if (!key) return all;
              assertSafeKey(key);

              all[key] = row[row.length - 1] ?? "";
              return all;
            }, {});

          resolve(words);
        } catch (error) {
          reject(error as Error);
        }
      },
      error: reject,
    });
  });
}

function normalizeImportedValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return String(value);
}

/**
 * Рекурсивно разворачивает вложенный JSON-объект переводов в плоскую карту
 * "key.subkey" → value (локальный аналог flatten из flatten-unflatten,
 * используемого в референс-проекте).
 *
 * Пустые/пробельные сегменты ключей отбрасываются, опасные (__proto__ и
 * компания) — отменяют импорт с понятной ошибкой (зеркало серверного гарда).
 */
export function flattenTranslationObject(
  data: unknown,
  prefix = ""
): Record<string, string> {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return prefix ? { [prefix]: normalizeImportedValue(data) } : {};
  }

  return Object.entries(data as Record<string, unknown>).reduce<
    Record<string, string>
  >((all, [key, value]) => {
    const trimmedKey = key.trim();

    if (!trimmedKey) return all;
    assertSafeKey(trimmedKey);

    const fullKey = prefix ? `${prefix}.${trimmedKey}` : trimmedKey;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(all, flattenTranslationObject(value, fullKey));
    } else {
      all[fullKey] = normalizeImportedValue(value);
    }

    return all;
  }, {});
}

/**
 * Читает JSON-файл и разворачивает его в плоскую карту key → value.
 */
export async function parseJsonFile(
  file: File
): Promise<Record<string, string>> {
  const text = await file.text();
  return flattenTranslationObject(JSON.parse(text));
}
